import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, Calculator, TrendingUp, Target, ChevronDown, ChevronUp, CheckCircle, Building2, BarChart3, ChevronRight, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../utils/mockData';

import usePageSeo from '../hooks/usePageSeo';
const Services = () => {
  usePageSeo({
    title: 'Our Services | Audit, Tax, Accounting & Advisory',
    description: 'Full range of UAE professional services — audit, Corporate Tax, VAT, accounting, company formation, goAML compliance and valuation by WPAA.',
    path: '/services'
  });

  const [expandedService, setExpandedService] = useState('audit');

  // Add Company Formation and Valuation to services
  const companyFormation = {
    title: "Company Formation",
    description: "End-to-end business setup services in UAE including licensing, legal structure, and regulatory compliance.",
    items: [
      { name: "Mainland Company Setup", description: "Complete setup assistance for LLC, branch offices, and representative offices in UAE mainland." },
      { name: "Free Zone Incorporation", description: "Business setup in UAE free zones with 100% ownership and tax benefits." },
      { name: "Trade License & Permits", description: "Assistance with trade license applications, activity approvals, and permit renewals." },
      { name: "PRO & Government Services", description: "Visa processing, labor cards, and all government-related documentation services." }
    ]
  };

  const valuationServices = {
    title: "Valuation Services",
    description: "Independent valuation services for transactions, financial reporting, compliance, and strategic decision-making.",
    items: [
      { name: "Business Valuation", description: "Fair value determination for M&A, shareholder restructuring, and investment planning." },
      { name: "Intangible Asset Valuation", description: "Valuation of brands, trademarks, customer relationships, and intellectual property." },
      { name: "Financial Reporting Valuation", description: "IFRS-compliant valuations for PPA, goodwill impairment, and fair value measurement." },
      { name: "Litigation Support", description: "Expert financial analysis for shareholder disputes and economic loss assessments." }
    ]
  };

  const smeBusinessPackage = {
    title: "SME Business Package",
    description: "All-in-one accounting, VAT, Corporate Tax, reporting and compliance support designed specifically for UAE SMEs.",
    items: [
      { name: "Accounting & Bookkeeping", description: "Day-to-day transaction recording on Zoho Books or QuickBooks tailored to SME workflows." },
      { name: "VAT & Corporate Tax Filing", description: "End-to-end VAT return filing and 9% Corporate Tax registration & filing support." },
      { name: "Financial Reporting & Reconciliation", description: "Monthly P&L, Balance Sheet, Cash Flow plus bank, supplier and customer reconciliations." },
      { name: "Audit Readiness & Advisory", description: "Audit-ready financial records and ongoing business advisory under one trusted team." }
    ]
  };

  const eInvoicing = {
    title: "E-Invoicing Readiness",
    description: "Preparation and coordination for UAE e-invoicing, from readiness assessment to go-live and ongoing reconciliation.",
    items: [
      { name: "Readiness Assessment & Gap Analysis", description: "Review of invoicing workflows, ERP integration options and invoice data gaps, with a practical roadmap." },
      { name: "ASP Selection & Onboarding", description: "Support choosing an accredited service provider and coordinating onboarding." },
      { name: "Invoice Data Mapping & VAT Review", description: "Review of invoice fields, VAT categories and tax codes, including PINT AE alignment." },
      { name: "Training & Ongoing Support", description: "Staff training, exception handling procedures and reconciliation with VAT returns." }
    ]
  };

  const serviceCategories = [
    { id: 'audit', ...services.audit, icon: FileCheck, color: 'teal', link: '/audit-services-uae' },
    { id: 'tax', ...services.tax, icon: Calculator, color: 'blue', link: '/corporate-tax-uae' },
    { id: 'accounting', ...services.accounting, icon: TrendingUp, color: 'green', link: '/accounting-services-uae' },
    { id: 'advisory', ...services.advisory, icon: Target, color: 'purple', link: '/business-advisory-uae' },
    { id: 'formation', ...companyFormation, icon: Building2, color: 'indigo', link: '/company-formation-uae' },
    { id: 'einvoicing', ...eInvoicing, icon: Calculator, color: 'blue', link: '/e-invoicing-uae' },
    { id: 'goaml', ...services.goaml, icon: FileCheck, color: 'orange', link: '/services/goaml-compliance' },
    { id: 'valuation', ...valuationServices, icon: BarChart3, color: 'rose', link: '/services/valuation' },
    { id: 'sme', ...smeBusinessPackage, icon: Package, color: 'amber', link: '/sme-business-services-uae' }
  ];

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50">
        <div className="container mx-auto text-center">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
          <h1 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
            Comprehensive Financial Solutions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From statutory audits to strategic advisory, we provide end-to-end services designed to drive your business success in the UAE market.
          </p>
        </div>
      </section>

      {/* Services Grid Overview */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {serviceCategories.map((category) => {
              const Icon = category.icon;
              const colorMap = {
                teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-500', hover: 'hover:border-teal-500' },
                blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-500', hover: 'hover:border-blue-500' },
                green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-500', hover: 'hover:border-green-500' },
                purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-500', hover: 'hover:border-purple-500' },
                orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-500', hover: 'hover:border-orange-500' },
                indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-500', hover: 'hover:border-indigo-500' },
                rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-500', hover: 'hover:border-rose-500' },
                amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-500', hover: 'hover:border-amber-500' }
              };
              const colors = colorMap[category.color];

              return (
                <Link
                  key={category.id}
                  to={category.link}
                  data-testid={`service-card-${category.id}`}
                  className="block"
                >
                  <Card
                    className={`border-2 ${colors.hover} transition-all hover:shadow-lg cursor-pointer group h-full`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`${colors.bg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className={colors.text} size={32} />
                      </div>
                      <h3 className={`text-xl font-bold text-gray-900 mb-2 group-hover:${colors.text} transition-colors`}>
                        {category.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <p className="text-gray-600 text-lg text-center max-w-3xl mx-auto -mt-10 mb-20" data-testid="services-accounting-note">
            Many clients start with the basics: accurate, up-to-date books. Our{' '}
            <Link to="/accounting-services-uae" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900" data-testid="services-accounting-link">
              accounting services in the UAE
            </Link>{' '}
            include monthly bookkeeping on Zoho Books or QuickBooks, reconciliations and management reporting, so audits, VAT returns and Corporate Tax filings rest on reliable numbers.
          </p>

          {/* Detailed Services Accordion */}
          <div className="max-w-5xl mx-auto space-y-6">
            {serviceCategories.map((category) => {
              const Icon = category.icon;
              const isExpanded = expandedService === category.id;
              const colorMap = {
                teal: 'from-teal-600 to-teal-700',
                blue: 'from-blue-600 to-blue-700',
                green: 'from-green-600 to-green-700',
                purple: 'from-purple-600 to-purple-700',
                orange: 'from-orange-600 to-orange-700',
                indigo: 'from-indigo-600 to-indigo-700',
                rose: 'from-rose-600 to-rose-700',
                amber: 'from-amber-600 to-amber-700'
              };
              const gradient = colorMap[category.color];

              return (
                <div key={category.id} className="border-2 border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-all">
                  <button
                    onClick={() => toggleService(category.id)}
                    className={`w-full p-6 flex items-center justify-between bg-gradient-to-r ${gradient} text-white hover:opacity-90 transition-opacity`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="bg-white/20 p-3 rounded-lg">
                        <Icon size={28} />
                      </div>
                      <div className="text-left">
                        <h3 className="text-2xl font-bold">{category.title}</h3>
                        <p className="text-sm opacity-90 mt-1">{category.description}</p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </button>

                  {isExpanded && (
                    <div className="bg-white p-8 animate-in slide-in-from-top-2">
                      <div className="grid md:grid-cols-2 gap-6">
                        {category.items.map((item, index) => (
                          <div key={index} className="bg-gray-50 p-6 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-start space-x-3">
                              <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">{item.name}</h4>
                                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {category.id === 'goaml' && (
                        <div className="mt-8 text-center">
                          <Link to="/services/goaml-compliance">
                            <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8">
                              Learn More About goAML Services
                              <ChevronRight size={20} className="ml-2" />
                            </Button>
                          </Link>
                        </div>
                      )}
                      {category.id === 'valuation' && (
                        <div className="mt-8 text-center">
                          <Link to="/services/valuation">
                            <Button className="bg-rose-600 hover:bg-rose-700 text-white px-8">
                              Learn More About Valuation Services
                              <ChevronRight size={20} className="ml-2" />
                            </Button>
                          </Link>
                        </div>
                      )}
                      {category.id === 'sme' && (
                        <div className="mt-8 text-center">
                          <Link to="/sme-business-services-uae">
                            <Button className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                              Learn More About SME Business Package
                              <ChevronRight size={20} className="ml-2" />
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Service Process</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A streamlined, transparent approach ensuring quality delivery and client satisfaction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Discuss your needs and objectives' },
              { step: '02', title: 'Proposal', desc: 'Detailed scope and transparent pricing' },
              { step: '03', title: 'Execution', desc: 'Quality service delivery with updates' },
              { step: '04', title: 'Follow-up', desc: 'Ongoing support and guidance' }
            ].map((process, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <div className="text-5xl font-bold text-teal-100 mb-4">{process.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{process.title}</h3>
                  <p className="text-gray-600">{process.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-teal-300"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Our Services?</h2>
              <div className="space-y-4">
                {[
                  { title: 'Expert Team', desc: 'Certified professionals with deep UAE market expertise' },
                  { title: 'Customized Solutions', desc: 'Tailored services addressing your specific needs' },
                  { title: 'Timely Delivery', desc: 'Committed to meeting deadlines and expectations' },
                  { title: 'Transparent Pricing', desc: 'Clear, competitive rates with no hidden costs' },
                  { title: 'Full Compliance', desc: '100% adherence to regulatory requirements' },
                  { title: 'Ongoing Support', desc: 'Continuous guidance beyond service completion' }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
                    <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-bold text-gray-900">{benefit.title}</h4>
                      <p className="text-sm text-gray-600">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop&q=75&fm=webp" loading="lazy"
                alt="Professional services"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Need Professional Services?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how our services can support your business goals and ensure compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Request a Quote
              </Button>
            </Link>
            <Link to="/team">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-6 text-lg">
                Meet Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
